import { useMemo, useState } from "react";
import { PALETTE, FONT_BODY, FONT_DISPLAY, FONT_MONO, track } from "./tokens";

/* Company-wide ROI strip. Owner picks number of employees on CoBrain
   and their average loaded annual cost. We show recovered capacity
   (~12% productivity uplift, conservative cohort range), then compare
   to the CoBrain monthly cost (per 10-employee block). */

const DEFAULT_UPLIFT = 12; // conservative — cohort range 8–18%

const fmt = (n: number) => `$${Math.round(n).toLocaleString("en-US")}`;

type Props = { employees?: number };

export const RoiStrip = ({ employees: employeesProp }: Props = {}) => {
  const employees = employeesProp ?? 20;
  const [salary, setSalary] = useState(85000);
  const [uplift, setUplift] = useState(DEFAULT_UPLIFT);

  const annualValue = useMemo(() => employees * salary * (uplift / 100), [employees, salary, uplift]);

  return (
    <div style={{
      background: PALETTE.card, border: `1px solid ${PALETTE.line}`,
      borderRadius: 24, padding: "clamp(24px, 4vw, 40px)",
      display: "grid", gridTemplateColumns: "minmax(0, 1.1fr) minmax(0, 0.9fr)",
      gap: 32, alignItems: "center", position: "relative", overflow: "hidden",
    }} className="cb-roi-grid">
      <style>{`
        @media (max-width: 760px) {
          .cb-roi-grid { grid-template-columns: 1fr !important; gap: 24px !important; }
        }
        .cb-roi-slider { -webkit-appearance: none; appearance: none; width: 100%; height: 4px; background: ${PALETTE.line}; border-radius: 999px; outline: none; }
        .cb-roi-slider::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 22px; height: 22px; border-radius: 50%; background: ${PALETTE.amber}; border: 3px solid #fff; box-shadow: 0 4px 10px -2px rgba(196,130,58,0.55); cursor: pointer; }
        .cb-roi-slider::-moz-range-thumb { width: 22px; height: 22px; border-radius: 50%; background: ${PALETTE.amber}; border: 3px solid #fff; box-shadow: 0 4px 10px -2px rgba(196,130,58,0.55); cursor: pointer; }
      `}</style>

      {/* Inputs */}
      <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
        <div style={{
          padding: "12px 14px", borderRadius: 12,
          background: "rgba(196,130,58,0.08)", border: "1px solid rgba(196,130,58,0.18)",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
        }}>
          <span style={{ fontFamily: FONT_MONO, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: PALETTE.muted, fontWeight: 700 }}>
            Employees on CoBrain
          </span>
          <span style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", fontSize: 26, color: PALETTE.ink, lineHeight: 1 }}>{employees}</span>
        </div>

        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
            <label htmlFor="cb-roi-sal" style={{ fontFamily: FONT_MONO, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: PALETTE.muted, fontWeight: 700 }}>
              Avg loaded employee cost / yr
            </label>
            <span style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", fontSize: 26, color: PALETTE.ink, lineHeight: 1 }}>{fmt(salary)}</span>
          </div>
          <input id="cb-roi-sal" type="range" min={45000} max={180000} step={5000} value={salary}
            onChange={(e) => { setSalary(Number(e.target.value)); track("roi_salary_change", { salary: Number(e.target.value) }); }}
            className="cb-roi-slider" />
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 11, color: PALETTE.muted, fontFamily: FONT_MONO }}>
            <span>$45k</span><span>$180k</span>
          </div>
        </div>

        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
            <label htmlFor="cb-roi-up" style={{ fontFamily: FONT_MONO, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: PALETTE.muted, fontWeight: 700 }}>
              Capacity uplift assumption
            </label>
            <span style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", fontSize: 26, color: PALETTE.ink, lineHeight: 1 }}>{uplift}%</span>
          </div>
          <input id="cb-roi-up" type="range" min={5} max={25} step={1} value={uplift}
            onChange={(e) => { setUplift(Number(e.target.value)); track("roi_uplift_change", { uplift: Number(e.target.value) }); }}
            className="cb-roi-slider" />
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 11, color: PALETTE.muted, fontFamily: FONT_MONO }}>
            <span>5%</span><span>cohort range 8–18%</span><span>25%</span>
          </div>
        </div>
      </div>

      {/* Output */}
      <div style={{
        background: PALETTE.dark, color: "#fff", borderRadius: 18,
        padding: "clamp(20px, 3vw, 28px)", position: "relative", overflow: "hidden",
      }}>
        <div aria-hidden style={{
          position: "absolute", inset: "-30% -10% auto auto", width: 320, height: 320,
          background: `radial-gradient(circle, ${PALETTE.amber}33 0%, transparent 60%)`,
          filter: "blur(40px)", pointerEvents: "none",
        }} />
        <div style={{ position: "relative", zIndex: 1 }}>
          <div style={{ fontFamily: FONT_MONO, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: PALETTE.amberSoft, fontWeight: 700, marginBottom: 14 }}>
            Capacity returned across the company
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", fontSize: "clamp(56px, 9vw, 88px)", lineHeight: 0.95, fontWeight: 400, letterSpacing: "-0.02em", whiteSpace: "nowrap", minHeight: 84, display: "inline-block" }}>{fmt(annualValue)}</span>
            <span style={{ fontFamily: FONT_MONO, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: PALETTE.amberSoft, fontWeight: 700 }}>/ yr recovered</span>
          </div>
          <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 10, fontSize: 14, color: "rgba(255,255,255,0.85)", fontFamily: FONT_BODY, lineHeight: 1.5 }}>
            <div><span style={{ color: PALETTE.amberSoft, fontWeight: 700 }}>~{uplift}%</span> productivity uplift across {employees} employees.</div>
            <div>Deployments start at <span style={{ color: PALETTE.amberSoft, fontWeight: 700 }}>$1,500/mo</span>, scoped to your team.</div>
            <div>vs. hiring 1 ops manager: <span style={{ color: PALETTE.amberSoft, fontWeight: 700 }}>$75–110k/yr loaded</span>.</div>
          </div>
          <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid rgba(255,255,255,0.10)", fontSize: 12, color: "rgba(255,255,255,0.45)", lineHeight: 1.5 }}>
            Beta cohort range: 8–18% productivity uplift in month one. Math is illustrative — your numbers will vary.
          </div>
        </div>
      </div>
    </div>
  );
};